/**
 * @description 页面滚动插件
 *  可配合 businessCommon/baseComponents/basePageSliderPage/basePageSliderPage 使用，控制最多只渲染两个页面
 * @example
 *
 *  public render() {
 *      const pageList = this.getSlidePages();
 *      return (
 *          <div className="slide-pages">
 *              <div className="slide-page-list j_slide-page-list">
 *                  <div className="slide-page-list-wrap j_slide-page-list-wrap">{pageList}</div>
 *              </div>
 *          </div>
 *      );
 *  }
 *
 *  public init() {
 *      this.options = {
 *          parentEl: '.j_slide-page-list',
 *          scrollEl: '.j_slide-page-list-wrap',
 *      };
 *      this.slider = new PageSlider(this.options);
 *      this.slider.on('page-slide-change', (args) => {
 *          this.setState({
 *              currentIndex: args.currentIndex,
 *              totalPage: args.totalPage,
 *              direction: args.direction,
 *          });
 *      });
 *  }
 */

import { $document, $window, setCss } from '@umu/umu-dom';
import { throttle, EventEmitterWrapper } from '@umu/umu-util';
import { PageDirection } from '@umu/umu-types';

export interface ISliderOptions {
    parentEl: string;
    scrollEl: string;
    duration?: number;
    mouseDistance?: number;
    touchDistance?: number;
    // [prop: string]: any;
}

interface IConfig {
    nodes: number[];
    count: number;
}

// @ts-ignore
const hasPointerSupport = navigator.msPointerEnabled;
const EVENT_DOWN = 'ontouchstart' in window ? 'touchstart' : hasPointerSupport ? 'MSPointerDown' : 'mousedown';
const EVENT_MOVE = 'ontouchmove' in window ? 'touchmove' : hasPointerSupport ? 'MSPointerMove' : 'mousemove';
const EVENT_UP = 'ontouchend' in window ? 'touchend' : hasPointerSupport ? 'MSPointerUp' : 'mouseup';

function getTouches(e: any) {
    const ret = { x: 0, y: 0 };
    if (EVENT_DOWN === 'touchstart') {
        e = e as TouchEvent;
        if (e.touches[0]) {
            ret.x = e.touches[0].clientX;
            ret.y = e.touches[0].clientY;
        }
    } else {
        e = e as MouseEvent;
        ret.x = e.clientX;
        ret.y = e.clientY;
    }
    return ret;
}

// via: https://developer.mozilla.org/zh-CN/docs/Web/API/EventTarget/addEventListener
let supportsPassive = false;
try {
    const opts = Object.defineProperty({}, 'passive', {
        get() {
            supportsPassive = true;
        },
    });
    $window.addEventListener(
        'test',
        () => {
            // nothing
        },
        opts,
    );
} catch (e) {
    // nothing
}

let startY = 0;
let lastY = 0;
// const resetPageSlideTimer = 0;

export class PageSlider extends EventEmitterWrapper {
    private options: ISliderOptions;
    private config: IConfig = {
        nodes: [],
        count: 0,
    };
    private el?: HTMLElement;
    private _currentIndex: number = 0;
    private _hasInstance: boolean = false;
    private scrollLocking: boolean = false;
    private lockedTimer: number = 0;
    private isGestureEnabled: boolean = true; // 禁止交互，但可以通过api切换
    private isSlideNext: boolean = true; // 禁止下滑，但可以通过api切换

    private resizeEvent = throttle(() => {
        // 微信webview要加个延时才取的正确
        setTimeout(() => {
            this.init();
            this.doSlide(PageDirection.Resize);
        }, 100);
    }, 200);

    constructor(options: ISliderOptions) {
        super();

        this.options = Object.assign(
            {
                duration: 0.4,
                mouseDistance: 20,
                touchDistance: 20,
            },
            options,
        );
    }

    get currentIndex(): number {
        return this._currentIndex;
    }

    set currentIndex(_currentIndex: number) {
        return;
    }

    get hasInstance(): number {
        return this.hasInstance;
    }

    set hasInstance(_hasInstance: number) {
        return;
    }

    public run = (index: number = 0) => {
        this.init();
        this.bindEvent();
        this.doSlide(index);
    };

    public slideNext = () => {
        this.doSlide(PageDirection.Next);
    };

    public slidePrev = () => {
        this.doSlide(PageDirection.Prev);
    };

    /**
     * index 以0为基的序号
     */
    public slideTo = (index: number) => {
        if (index < 0 || index > this.config.count) {
            console.error('超出范围');
            return;
        }
        const delta = index - this.currentIndex;
        if (delta > 0) {
            this.doSlide(PageDirection.Next, delta);
        } else if (delta < 0) {
            this.doSlide(PageDirection.Prev, delta);
        } else {
            this.doSlide(PageDirection.Resize);
        }
    };

    public enableInteractive = () => {
        this.isGestureEnabled = true;
    };

    public disableInteractive = () => {
        this.isGestureEnabled = false;
    };

    public disableSlideNext = () => {
        this.isSlideNext = false;
    };

    public enableSlideNext = () => {
        this.isSlideNext = true;
    };

    public destroy = () => {
        if (!this.el || !this._hasInstance) {
            return;
        }

        clearTimeout(this.lockedTimer);

        $window.removeEventListener('resize', this.resizeEvent, false);
        $window.removeEventListener('orientationChange', this.resizeEvent, false);
        $window.removeEventListener('keydown', this.keydownEvent, false);

        // mousewheel
        $document.removeEventListener(EVENT_MOVE, this.preventPageScroll, false);

        this.el.removeEventListener('mousewheel', this.mousewheelEvent, false);
        this.el.removeEventListener('DOMMouseScroll', this.mousewheelEvent, false);

        // touch
        this.el.removeEventListener(EVENT_DOWN, this.touchEventDown, false);
    };

    private getParentEl = () => {
        return $document.querySelector(this.options.parentEl) as HTMLElement;
    };

    private getScrollEl = () => {
        return $document.querySelector(this.options.scrollEl) as HTMLElement;
    };

    private init = () => {
        const parentEl = this.getParentEl();
        if (!parentEl) {
            console.error(`找不到 ${this.options.parentEl} 元素`);
            return;
        }

        this.el = parentEl;

        const screenHeight = $window.innerHeight;
        setCss(parentEl, {
            height: screenHeight,
            overflow: 'hidden',
        });
        const scrollEl = this.getScrollEl();
        setCss(scrollEl, {
            height: screenHeight,
        });

        let pageHeight = 0;
        const pages = [].slice.call(scrollEl.children);
        pages.forEach((el: HTMLElement, _index: number) => {
            setCss(el, {
                height: screenHeight,
            });
            pageHeight += screenHeight;
        });

        const arr = [0];
        const lastPageHeight = pageHeight % screenHeight;
        const pageCount = Math.floor(pageHeight / screenHeight);
        const pageTotal = lastPageHeight === 0 ? pageCount : Math.ceil(pageHeight / screenHeight);

        for (let i = 0; i < pageCount - 1; i++) {
            arr.push(screenHeight + arr[i]);
        }

        if (lastPageHeight) {
            arr.push(lastPageHeight + arr[pageCount - 1]);
        }

        this.config.nodes = arr;
        this.config.count = pageTotal;

        this._hasInstance = true;
    };

    private doSlide = (direction: PageDirection = PageDirection.Resize, delta: number = 1) => {
        if (this.scrollLocking) {
            return;
        }

        this.lockSlide();

        delta = Math.abs(delta);
        this._currentIndex += direction * delta;
        const max = this.config.count - 1;
        if (this._currentIndex < 0 || this._currentIndex === 0) {
            this._currentIndex = 0;
        } else if (this._currentIndex > max) {
            this._currentIndex = max;
        }

        // 广播事件
        this.emit('page-slide-change', {
            currentIndex: this._currentIndex,
            direction,
            totalPage: this.config.count,
        });

        // 翻页
        this.seetPageCss();
    };

    private seetPageCss = (offset: number = 0) => {
        const transformValue = `translate3D(0, -${this.config.nodes[this._currentIndex] + offset}px, 0)`;
        const scrollEl = this.getScrollEl();

        setCss(scrollEl, {
            transform: transformValue,
            transition: `transform ${offset ? 0 : this.options.duration}s ease`,
        });
    };

    private lockSlide = () => {
        this.scrollLocking = true;
        this.lockedTimer = $window.setTimeout(() => {
            this.scrollLocking = false;
        }, (this.options.duration || 0) * 1000 * 1.5);
    };

    private bindEvent = () => {
        if (!this.el || !this._hasInstance) {
            return;
        }

        $window.addEventListener('resize', this.resizeEvent, false);
        $window.addEventListener('orientationChange', this.resizeEvent, false);
        $window.addEventListener('keydown', this.keydownEvent, false);

        // mousewheel
        $document.addEventListener(EVENT_MOVE, this.preventPageScroll, supportsPassive ? { passive: false } : false);

        this.el.addEventListener('mousewheel', this.mousewheelEvent, false);
        this.el.addEventListener('DOMMouseScroll', this.mousewheelEvent, false);

        // touch
        this.el.addEventListener(EVENT_DOWN, this.touchEventDown, false);
    };

    private keydownEvent = (e: KeyboardEvent) => {
        switch (e.keyCode || e.which || e.charCode) {
            case 37: // left
            case 38: // up
                this.doSlide(PageDirection.Prev);
                e.preventDefault();
                break;
            case 39: // right
            case 40: // down
                this.doSlide(PageDirection.Next);
                e.preventDefault();
                break;
        }
    };

    // `any` for DOMMouseScroll type checking
    private mousewheelEvent = (e: MouseEvent | any) => {
        if (!this.isGestureEnabled) {
            return false;
        }

        const distance = this.options.mouseDistance!;
        // 20 for DOMMouseScroll, it's delta is between -3 and 3
        const delta = Math.max(-distance, Math.min(distance, e.wheelDelta || -e.detail * 20));
        // console.log(delta);
        const debounce = delta / distance;
        if (Math.abs(debounce) === 1) {
            // console.log(debounce);
            if (!this.isSlideNext && debounce < 0) {
                return;
            }
            this.doSlide(-debounce);
        }
        e.preventDefault();
        e.stopPropagation();
        return false;
    };

    private preventPageScroll = (e: MouseEvent | any) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
    };

    // touch events
    private touchEventDown = (e: Event) => {
        this.touchEventUnload();
        this.touchEventLaunch();

        const { y } = getTouches(e);
        startY = y;
        lastY = startY;
    };

    private touchEventMove = (e: Event | MouseEvent | TouchEvent) => {
        if (!this.isGestureEnabled) {
            return;
        }

        const { y } = getTouches(e);
        lastY = y;

        // $window.clearTimeout(resetPageSlideTimer);
        // resetPageSlideTimer = $window.setTimeout(this.resetPageSlide, (this.options.duration || 0.5) * 1.2 * 1000);

        // if (startY) {
        //     this.seetPageCss(startY - lastY);
        // }
        const distance = this.options.touchDistance!;
        const delta = lastY - startY;
        // console.log(delta);
        if (delta !== 0) {
            if (Math.abs(delta) > distance) {
                if (delta < 0) {
                    if (!this.isSlideNext) {
                        return;
                    }
                    this.doSlide(PageDirection.Next);
                } else {
                    this.doSlide(PageDirection.Prev);
                }
                // } else {
                //     this.resetPageSlide();
            }
        }
    };

    private touchEventUp = (_e: Event | MouseEvent | TouchEvent) => {
        // const distance = this.options.touchDistance;
        // const delta = lastY - startY;
        // // console.log(delta);
        // if (delta !== 0) {
        //     if (Math.abs(delta) > distance) {
        //         if (delta < 0) {
        //             this.doSlide(PageDirection.Next);
        //         } else {
        //             this.doSlide(PageDirection.Prev);
        //         }
        //     } else {
        //         this.resetPageSlide();
        //     }
        // }
        this.touchEventUnload();
    };

    // private resetPageSlide = () => {
    //     this.doSlide(PageDirection.Resize);
    // }

    private touchEventLaunch = () => {
        if (!this.el) {
            return;
        }

        this.el.addEventListener(EVENT_MOVE, this.touchEventMove, false);
        this.el.addEventListener(EVENT_UP, this.touchEventUp, false);
    };

    private touchEventUnload = () => {
        if (!this.el) {
            return;
        }

        this.el.removeEventListener(EVENT_MOVE, this.touchEventMove, false);
        this.el.removeEventListener(EVENT_UP, this.touchEventUp, false);

        startY = 0;
        lastY = 0;
    };
}

export default PageSlider;
